import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import Text from "@/components/Home/v2/shared/Text";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * ProductHeroV2 — hero for product pages rebuilt on the HomeV2 system.
 *
 * Anatomy (top→bottom):
 *   • Pill eyebrow chip: icon + category label (e.g. "📘 Online Courses").
 *     The pill's icon color is configurable per product.
 *   • Two-tone headline ("Create Online Courses" / "With Ease") — split
 *     into muted + bright lines, matching HomeV2.
 *   • Description paragraph.
 *   • Optional 4-up stats row (200,000+ / $1.5B+ / 5M+ / 150+).
 *   • CTA pair (Start Free Trial / Book A Demo).
 *   • Full-width hero image/illustration that bleeds into the gradient wash.
 *
 * The gradient wash behind the hero is applied by the parent ProductV2
 * wrapper (same pattern as HomeV2's purple/yellow wash) so the navbar + hero
 * read as one continuous background.
 */

export type ProductHeroStatProps = {
  value: string;
  label: string;
};

export type ProductHeroV2Props = {
  titleMuted: string;
  titleBright: string;
  description: string;
  stats?: ProductHeroStatProps[];
  ctas?: CtaType[];
  /** Hero image path (bleed image under the CTAs). Optional — the slot
   *  still renders with a soft gradient placeholder if absent. */
  heroImage?: string;
  // Legacy fields kept optional so existing data files don't break — they
  // are no longer rendered (the eyebrow pill was removed per design).
  eyebrow?: string;
  eyebrowIcon?: IconDefinition;
  eyebrowColorClass?: string;
};

const ProductHeroV2 = ({
  titleMuted,
  titleBright,
  description,
  stats,
  ctas,
  heroImage,
}: ProductHeroV2Props) => {
  return (
    <section className="relative w-full overflow-hidden bg-white">
      {/* Bottom-of-hero gradient wash — peach→pink spray behind the hero
          image. Sits under the copy + CTAs + image slot so the image bleeds
          into a soft color band. Matches the reference screenshot where
          the gradient only lives in the lower half of the hero (not
          under the navbar). */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[65%]"
        style={{
          background:
            "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 228, 214, 0.55) 45%, rgba(255, 228, 214, 0.35) 100%)," +
            "radial-gradient(ellipse 55% 80% at 0% 100%, rgba(255, 180, 210, 0.65) 0%, rgba(255, 180, 210, 0.1) 55%, rgba(255, 255, 255, 0) 85%)," +
            "radial-gradient(ellipse 55% 80% at 100% 100%, rgba(255, 210, 160, 0.55) 0%, rgba(255, 210, 160, 0.1) 55%, rgba(255, 255, 255, 0) 85%)",
        }}
      />
      <div
        className={
          heroImage
            ? "container relative z-10 mx-auto px-4 pb-0 pt-10 sm:px-6 sm:pt-12 md:px-8 md:pt-16 lg:px-12 lg:pt-20"
            : "container relative z-10 mx-auto px-4 pb-10 pt-10 sm:px-6 sm:pt-12 md:px-8 md:pb-14 md:pt-16 lg:px-12 lg:pb-20 lg:pt-20"
        }
      >
        <div className="mx-auto max-w-4xl text-center">
          <Text
            as="h1"
            variant="display-lg"
            className="text-[28px] sm:text-[32px] md:text-[40px] lg:text-[48px]"
          >
            {/* Per user: entire heading is neutralBlack — no muted grey half.
                Two-line break mirrors HomeV2's hero so product hero titles
                render on two lines (matches graphy.com reference). */}
            <span className="text-neutralBlack">{titleMuted}</span>
            {titleBright ? <br /> : null}
            {titleBright ? (
              <span className="text-neutralBlack">{titleBright}</span>
            ) : null}
          </Text>

          <Text
            as="p"
            variant="body-lg"
            className="mx-auto mt-4 max-w-2xl text-[14px] text-darkSlate04 md:mt-5 md:text-[16px]"
            style={{ letterSpacing: "-0.08px" }}
          >
            {description}
          </Text>
        </div>

        {stats && stats.length ? (
          // Stats row — transparent (sits on the gradient wash) so it reads
          // as an inline set of numbers rather than a card. Typography
          // mirrors HomeV2's hero stats (title-xl value + body-sm label).
          <div className="mx-auto mt-10 grid max-w-4xl grid-cols-2 gap-4 sm:gap-4 md:mt-12 md:grid-cols-4 md:gap-4">
            {stats.map((s) => (
              <div key={s.label} className="min-w-0 text-center">
                <div className="font-inter font-medium tracking-[-0.3px] text-neutralBlack text-[16px] leading-tight sm:text-[18px] sm:tracking-[-0.5px] md:text-[28px] md:tracking-[-0.6px] md:leading-none">
                  {s.value}
                </div>
                <div className="font-inter font-normal text-darkSlate04 mt-1 text-[11px] leading-tight sm:mt-2 sm:text-[12px] md:text-[14px]">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {ctas && ctas.length ? (
          <div className="mt-10 flex items-center justify-center md:mt-12">
            <div className="w-full max-w-[480px] [&>div]:!w-full [&_a]:w-full [&_button]:w-full">
              <CtaButtonGroup
                ctas={ctas}
                gap="gap-3"
                fullWidthOnMobile={true}
              />
            </div>
          </div>
        ) : null}

        {/* Hero image — bleeds into the gradient wash below. This is the
            LCP element on V2 product/feature/alternative pages, so flag
            it as high priority so the browser starts the request
            alongside the document instead of after stylesheet/JS
            parsing. */}
        {heroImage ? (
          <div className="mx-auto mt-4 w-full max-w-6xl md:mt-6">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={heroImage}
              alt=""
              aria-hidden="true"
              decoding="async"
              {...({ fetchpriority: "high" } as any)}
              className="block w-full"
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default ProductHeroV2;
